/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import React, { Fragment, useEffect, useRef } from 'react'
import { defineMessages, FormattedMessage } from 'react-intl'
import { Link } from 'react-router-dom'
import cx from 'classnames'
import { css, jsx } from '@emotion/react'
import { useDispatch } from 'react-redux'

import {
  EuiErrorBoundary,
  EuiPage,
  EuiPageBody,
  EuiPageSection,
  useEuiTheme,
  withEuiTheme,
} from '@elastic/eui'

import type { User } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState } from '@modules/ui-types'
import { themedBoxShadow } from '@modules/cui/styles/boxShadow'
import { BreadcrumbsContext } from '@modules/cui/Breadcrumbs'
import { logoutUrl } from '@modules/auth/urls'
import { useEulaStateQuery } from '@modules/eula/hooks'
import UnhandledApplicationLoadError from '@modules/app/UnhandledApplicationLoadError'

import SchedulerMiddleware from '@/lib/schedule/SchedulerMiddleware'
import { fetchEolStatusIfNeeded } from '@/actions/eolStatus'
import { fetchCurrentUser, resetFetchCurrentUser } from '@/actions/currentUser'

import { AppRouterContext } from '../../../../components/AppRouter'
import AppLoadingRoot from '../../../../components/AppLoadingRoot'
import ChromeNavigation from '../../../../components/ChromeNavigation'
import ChromeHeader from '../../../../components/ChromeHeader'
import RefreshApiToken from '../../../../components/RefreshApiToken'
import Eula from '../../../../components/Eula'
import { EnsureApiBaseUrl } from '../../../../components/ApiBaseUrl'
import PhoneHomeData from '../../../../components/PhoneHome/Data'

import type { ReactNode } from 'react'

const messages = defineMessages({
  rootFailed: {
    id: `app-index.fetching-the-root-resource-failed-link`,
    defaultMessage: `Fetching the root resource failed. {logOut}`,
  },
  permissionsFailed: {
    id: `app-index.fetching-user-permissions-failed-link`,
    defaultMessage: `Fetching user permissions failed. {logOut}`,
  },
  logOut: {
    id: `app-index.log-out`,
    defaultMessage: `Log out`,
  },
})

export interface Props {
  children?: ReactNode
  fetchCurrentUserRequest: AsyncRequestState
  isEulaAccepted: boolean
  pollingInterval: number
  requireEula: boolean
  currentUser: User | null
  isCreateUrl: boolean
  isGettingStartedUrl: boolean
}

const App: React.FunctionComponent<Props> = ({
  children,
  fetchCurrentUserRequest,
  requireEula,
  currentUser,
  isCreateUrl,
  isGettingStartedUrl,
}) => {
  const theme = useEuiTheme()
  const dispatch = useDispatch()

  const breadcrumbsRef = useRef<HTMLDivElement>(null)
  const announcementsBreadcrumbsRef = useRef<HTMLDivElement>(null)

  const eulaStatusQuery = useEulaStateQuery({ enabled: requireEula })

  useEffect(() => {
    dispatch(fetchEolStatusIfNeeded())

    if (!currentUser) {
      dispatch(fetchCurrentUser())
    }

    return () => {
      dispatch(resetFetchCurrentUser())
    }
  }, [currentUser, dispatch])

  if (requireEula && eulaStatusQuery.isError) {
    return (
      <UnhandledApplicationLoadError error={eulaStatusQuery.error as Error}>
        <FormattedMessage
          id='app-index.fetching-the-root-resource-failed-link'
          defaultMessage='Fetching the root resource failed. {logOut}'
          values={{
            logOut: (
              <Link to={logoutUrl()}>
                <FormattedMessage id='app-index.log-out' defaultMessage='Log out' />
              </Link>
            ),
          }}
        />
      </UnhandledApplicationLoadError>
    )
  }

  if (fetchCurrentUserRequest.error) {
    return (
      <UnhandledApplicationLoadError error={fetchCurrentUserRequest.error as Error | undefined}>
        <FormattedMessage
          {...messages.permissionsFailed}
          values={{
            logOut: (
              <Link to={logoutUrl()}>
                <FormattedMessage id='app-index.log-out' defaultMessage='Log out' />
              </Link>
            ),
          }}
        />
      </UnhandledApplicationLoadError>
    )
  }

  if (requireEula && eulaStatusQuery.isSuccess && !eulaStatusQuery.data.accepted) {
    return <Eula />
  }

  if (!currentUser) {
    return <AppLoadingRoot />
  }

  const isGettingStartedRoute = isCreateUrl || isGettingStartedUrl

  const cloudContentStyle = css`
    background: ${!isGettingStartedRoute
      ? theme.euiTheme.colors.emptyShade
      : theme.euiTheme.colors.body};
    box-shadow: ${themedBoxShadow({ theme })};
  `

  return (
    <SchedulerMiddleware>
      <EnsureApiBaseUrl>
        <Fragment>
          <ChromeHeader
            breadcrumbsRef={breadcrumbsRef}
            announcementsRef={announcementsBreadcrumbsRef}
          />

          <EuiPage paddingSize='none'>
            {!isGettingStartedRoute && (
              <aside className='cloudSidebar'>
                <AppRouterContext.Consumer>
                  {({ routes }) => <ChromeNavigation routes={routes} />}
                </AppRouterContext.Consumer>
              </aside>
            )}

            <div
              className={cx('cloudContent', { createPage: isGettingStartedRoute })}
              id='cloudPortalPage'
              css={cloudContentStyle}
            >
              <EuiPageBody>
                <EuiPageSection className='cloudContentBody' paddingSize='m' color='transparent'>
                  <div data-app='appContentBody'>
                    <PhoneHomeData />

                    <EuiErrorBoundary>
                      <BreadcrumbsContext.Provider
                        value={{
                          breadcrumbsRef,
                          announcementsRef: announcementsBreadcrumbsRef,
                        }}
                      >
                        {children}
                      </BreadcrumbsContext.Provider>
                    </EuiErrorBoundary>
                  </div>
                </EuiPageSection>
              </EuiPageBody>
            </div>
          </EuiPage>
        </Fragment>
      </EnsureApiBaseUrl>

      <RefreshApiToken />
    </SchedulerMiddleware>
  )
}

export default withEuiTheme(App)
